import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import PricingPlanCard, { formatPlanPrice, defaultPlanId } from './PricingPlanCard';
import { useCountryPricing } from '../hooks/useCountryPricing';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { safeArray } from '../lib/utils';
import { getApiBaseUrl } from '@/lib/apiBase';

const API_URL = getApiBaseUrl();

// Home page #pricing card — the same PricingPlanCard /pricing renders, fed from
// the same GET /settings/plans, so plans/prices/features always match. No
// checkout here: the CTA hands the chosen plan to /pricing (?plan=<id>), or to
// signup for logged-out visitors (same top-of-funnel target as before).
export default function HomePricingCard({ settings }) {
  const { user } = useAuth();
  const { t, tFormat } = useTranslation();
  const { pricing: countryPricing, countryCode, setCountry } = useCountryPricing();
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/country-pricing`)
      .then(({ data }) => setCountries(safeArray(data).filter((c) => c.enabled)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!countryCode) return;
    axios.get(`${API_URL}/settings/plans`, { params: { country_code: countryCode } })
      .then(({ data }) => {
        const list = safeArray(data?.plans);
        setPlans(list);
        setSelectedPlanId((prev) => defaultPlanId(list, prev));
      })
      .catch(() => setPlans([]));
  }, [countryCode]);

  const isIndia = countryCode === 'IN';
  const plan = plans.find((p) => p.id === selectedPlanId);
  if (!plan) return null;

  const formatPrice = (price) => formatPlanPrice(price, {
    isIndia,
    symbol: plan.currency_symbol || countryPricing?.currency_symbol || '',
  });
  const isDatedPlan = plan.id !== 'lifetime';
  const target = user ? `https://thachari.com/pricing?plan=${plan.id}` : 'https://thachari.com/signup';

  return (
    <PricingPlanCard
      plans={plans}
      selectedPlanId={plan.id}
      onSelectPlan={setSelectedPlanId}
      lifetimeBadge={plan.badge || settings?.lifetime_badge_text || 'BEST VALUE'}
      headerExtra={countries.length > 1 && (
        <select
          value={countryCode}
          onChange={(e) => setCountry(e.target.value)}
          className="text-xs border rounded-full px-2.5 py-1 bg-background text-muted-foreground cursor-pointer"
          aria-label={t('pricing_country_switcher', 'Change country')}
        >
          {countries.map((c) => (
            <option key={c.country_code} value={c.country_code}>
              {c.country_name} ({c.currency_code})
            </option>
          ))}
        </select>
      )}
      price={plan.base_price}
      originalPrice={plan.original_price}
      taxRate={plan.tax_rate}
      taxLabel={plan.tax_label}
      total={plan.total_with_tax}
      isIndia={isIndia}
      formatPrice={formatPrice}
    >
      <Button
        onClick={() => { window.location.href = target; }}
        onTouchStart={() => { window.location.href = target; }}
        className="w-full rounded-full bg-[#A67C52] hover:bg-[#8f6a45] text-white"
        data-testid="home-pricing-cta"
      >
        {isDatedPlan
          ? tFormat('home_pricing_cta_plan', { name: plan.name }, 'Get the {name} Plan')
          : t('pricing_get_lifetime_btn', 'Get Premium Access')}
      </Button>
      {settings?.enable_coupons !== false && (
        <p className="text-center text-xs text-muted-foreground mt-2">
          {t('home_pricing_coupon_note', 'Have a coupon? Apply it at checkout.')}
        </p>
      )}
    </PricingPlanCard>
  );
}
